/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */

import { css, html, LitElement } from 'lit-element';

/**
 * @prop {Boolean} number - If set to `true`, the text will align to the right.
 * @prop {Boolean} icon - If set to `true`, the cell will have a fixed width (2rem) and sorting won't apply.
 * @prop {String} minWidth - If a value in px is defined, the cell won't scale horizontally below that value, and horizontal scrolling may be applied.
 * @attr {String} min-width - If a value in px is defined, the cell won't scale horizontally below that value, and horizontal scrolling may be applied.
 * @prop {Boolean} checkbox - 	If set to `true`, the cell will have a fixed width and checkbox can be placed inside to have it in the first column for checkbox multi-selection.
 * @slot Takes any component or text string as children.
 */
export class SHTableCell extends LitElement {
  static get properties() {
    return {
      number: { type: Boolean, reflect: true },
      icon: { type: Boolean, reflect: true },
      /** @ignore */
      condensed: { type: Boolean, reflect: true },
      /** @ignore */
      minWidth: { type: String, reflect: true, attribute: 'min-width' },
      /** @ignore */
      columns: { type: String, reflect: true },
      /** @ignore */
      colS: { type: String, reflect: true, attribute: 'col-s' },
      /** @ignore */
      colM: { type: String, reflect: true, attribute: 'col-m' },
      checkbox: { type: Boolean, reflect: true },
    };
  }

  static get styles() {
    return [
      css`
        :host {
          display: inline-flex;
          box-sizing: border-box;
          height: fit-content;
          height: -ms-fit-content;
          height: -moz-fit-content;
          align-items: center;
        }
        * {
          box-sizing: border-box;
        }
        .cell-wrapper {
          width: 100%;
          display: flex;
          flex-direction: column;
          font: var(--body-1);
          margin: 0.5rem 0.75rem;
          line-height: 2rem;
          color: var(--text-primary);
          word-wrap: break-word;
          cursor: default;
          width: calc(100% - 1.5rem);
          height: fit-content;
          height: -ms-fit-content;
          height: -moz-fit-content;
          font-weight: normal;
          -webkit-font-smoothing: antialiased !important;
          text-rendering: optimizeLegibility !important;
          -moz-osx-font-smoothing: grayscale !important;
        }
        /* number cells */
        :host([number]) .cell-wrapper {
          text-align: end;
          word-break: break-word;
        }
        /* icon cells */
        :host([icon]),
        :host([checkbox]) {
          flex: 0;
          min-width: 3.5rem !important;
        }
        :host([checkbox]) {
          margin-top: 0.2rem;
        }
        /* medium screens */
        @media only screen and (max-width: 64.0625rem) {
          :host([icon]),
          :host([checkbox]) {
            min-width: 3rem !important;
          }
          .cell-wrapper {
            margin: 0.5rem 0.5rem;
            width: calc(100% - 1rem);
          }
        }
        /* condensed */
        :host([condensed]) .cell-wrapper {
          margin: 0.25rem 0.75rem;
        }
      `,
    ];
  }

  render() {
    return html`
      <div class="cell-wrapper">
        <slot></slot>
      </div>
    `;
  }

  constructor() {
    super();
    this.minWidth = '2.5rem';
  }

  connectedCallback() {
    super.connectedCallback();
    const icons = this.querySelectorAll('sh-icon');
    if (icons) {
      for (let i = 0; i < icons.length; i++) {
        if (icons[i].button && !icons[i].hasAttribute('tabindex')) {
          icons[i].setAttribute('tabindex', 0);
        }
      }
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('minWidth')) {
      this.style.minWidth = this.minWidth;
    }
  }
}

customElements.define('sh-table-cell', SHTableCell);
